import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Navigate, useLocation } from 'react-router';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import * as Yup from 'yup';
import { MerchantTitle } from 'app/MerchantTitle';
import { OrderScenario } from 'components/order/model/Order';
import { getLocalMerchantId } from 'components/settings/localStore';
import { getOnCloseRedirectUrl, getUserId } from 'components/user/localAuth';
import { User } from 'components/user/model/User';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { TextFormField } from 'lib/form/TextFormField';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { activate, clearErrors } from 'store/auth/authSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { ROUTES } from './routes';

const PREFIX = 'ActivatePage';

const classes = {
    root: `${PREFIX}root`,
    button: `${PREFIX}button`
};

const Root = styled('div')(({ theme }) => ({
    [`& .${classes.root}`]: {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(4)
    },

    [`& .${classes.button}`]: {
        height: '48px',
        fontSize: '1.2em'
    }
}));

interface ActivateFormData {
    code: string;
}

const initialFields: ActivateFormData = {
    code: ''
};

const isLoading = false;

export const ActivatePage: React.FC = () => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const { getParsedPath, push } = useLocalHistory();
    const { id, user, activateError } = useSelector((state: ApplicationState) => state.auth);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { locationId } = useSelector((state: ApplicationState) => state.basket);
    const [scenario] = useRouteScenario();
    const { isGuest, isLoggedIn } = useAuth();
    const location = useLocation();
    const locationState = location.state as { user: User };
    const signinValidation = React.useMemo(
        () =>
            Yup.object().shape({
                code: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            }),
        [t]
    );
    const userId = React.useMemo(() => id || getUserId(), [id]);
    const handleSubmit = React.useCallback(
        (data: ActivateFormData) => dispatch(activate({ id: userId, token: String(data.code) })),
        [userId, dispatch]
    );
    const shouldEnterEmail = React.useMemo(
        () => !!settings && settings.emailAuthEnabled && settings.phoneAuthEnabled,
        [settings]
    );
    React.useEffect(() => {
        if (isLoggedIn && !isGuest) {
            const merchantId = getLocalMerchantId();
            if (merchantId) {
                if (shouldEnterEmail) {
                    push(getParsedPath(ROUTES.USER.EMAIL_CAPTURE, { merchantId }));
                } else {
                    const returnUrl = getOnCloseRedirectUrl(merchantId);
                    if (returnUrl) {
                        const [path, search] = returnUrl.split('?');
                        push(path, {}, search);
                    } else if (scenario === OrderScenario.TABLE) {
                        push(ROUTES.JOURNEY.LANDING, { merchantId, locationId });
                    } else {
                        push(ROUTES.BASE);
                    }
                }
            }
        }
    }, [user, push, isGuest, isLoggedIn, getParsedPath, scenario, locationId, shouldEnterEmail]);
    React.useEffect(
        () => () => {
            dispatch(clearErrors());
        },
        [dispatch]
    );

    const handleBack = React.useCallback(() => {
        if (locationState && locationState.user) {
            push(ROUTES.USER.REGISTER, {}, '', { user: locationState.user, identity: userId });
        }
    }, [locationState, push, userId]);

    if (!userId) {
        return <Navigate to={getParsedPath(ROUTES.USER.LOGIN)} />;
    }

    return (
        <Root>
            <MerchantTitle title={t('ONBOARDING_ACTIVATION_TITLE')} />
            <MuiAppBar title={t('ONBOARDING_ACTIVATION_TITLE')} onBack={handleBack} />
            <Formik initialValues={initialFields} validationSchema={signinValidation} onSubmit={handleSubmit}>
                {({ submitForm, isSubmitting, isValid, values: { code } }) => (
                    <Form className={classes.root}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography>{t('ONBOARDING_ACTIVATION_MESSAGE')}</Typography>
                            </Grid>
                            <Grid size={12}>
                                <TextFormField
                                    name="code"
                                    type="number"
                                    label={t('ONBOARDING_ACTIVATION_CODE')}
                                />
                            </Grid>
                            {!!activateError && (
                                <Grid size={12}>
                                    <Typography color="error">{activateError}</Typography>
                                </Grid>
                            )}
                            <Grid size={12}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    disabled={!isValid || isLoading || !code || isSubmitting}
                                    fullWidth
                                    onClick={submitForm}
                                    className={classes.button}
                                    loading={isSubmitting}
                                >
                                    {t('BUTTON_SUBMIT')}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Root>
    );
};
